'use client';
import { BookOpen, Lightbulb, Wrench } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useT } from '../../i18n';
import { quotes } from '../../content/quotes';

const PhilosophySection = () => {
  const t = useT();
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);


  // Auto-rotate quotes
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuoteIndex((prev) => (prev + 1) % quotes.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [quotes.length]);

  const pillars = [
    {
      icon: <BookOpen className="w-7 h-7" />,
      title: t('philosophy.pillar1.title'),
      description: t('philosophy.pillar1.desc'),
      color: 'from-jkd-gold to-yellow-300',
    },
    {
      icon: <Lightbulb className="w-7 h-7" />,
      title: t('philosophy.pillar2.title'),
      description: t('philosophy.pillar2.desc'),
      color: 'from-yellow-300 to-jkd-gold',
    },
    {
      icon: <Wrench className="w-7 h-7" />,
      title: t('philosophy.pillar3.title'),
      description: t('philosophy.pillar3.desc'),
      color: 'from-jkd-gold to-orange-400',
    },
  ];

  const currentQuote = quotes[currentQuoteIndex];

  // Main Section (Form Step)
  return (
    <section id="philosophy" className="py-12 lg:py-16 bg-jkd-gray-100/30 relative overflow-hidden">
      {/* Background Quote */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5">
        <span className="text-[20vw] font-black text-jkd-gold text-center leading-none">
          BE WATER
        </span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-2 bg-jkd-black text-jkd-gold text-sm font-mono tracking-widest mb-4 rounded">
            {t('philosophy.tag')}
          </span>
          <h2 className="section-title text-white">
            <span className="text-gradient-gold">{t('philosophy.title')}</span>
          </h2>
          <p className="section-subtitle mx-auto mt-4">
            {t('philosophy.subtitle')}
          </p>
          <div className="divider-gold mx-auto mt-6" />
        </div>

        {/* Quote Carousel */}
        <div className="mb-8">
          <div className="max-w-3xl mx-auto text-center">
            <div 
              className="relative p-6 sm:p-8 bg-jkd-black/50 rounded-lg border border-jkd-gray-200 min-h-[180px] flex flex-col justify-center transition-all duration-500"
              key={currentQuoteIndex}
            >
              <p className="text-lg sm:text-xl md:text-2xl font-light text-white mb-3 italic animate-fade-in">
                "{currentQuote.text}"
              </p>
              <p className="text-sm sm:text-base text-gray-300 mb-2 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                {currentQuote.translation}
              </p>
              <cite className="text-jkd-gold not-italic animate-fade-in" style={{ animationDelay: '0.2s' }}>
                — {currentQuote.speaker}
              </cite>
            </div>
            {/* Quote Indicators */}
            <div className="flex justify-center gap-1.5 mt-4 flex-wrap">
              {quotes.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentQuoteIndex(index)}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                    index === currentQuoteIndex ? 'bg-jkd-gold w-4' : 'bg-jkd-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Three Pillars */}
        <div className="grid md:grid-cols-3 gap-6">
          {pillars.map((pillar, index) => (
            <div
              key={index}
              className="group card-dark text-center hover:border-jkd-gold/50 transition-all duration-300"
            >
              <div
                className={`inline-flex items-center justify-center w-14 h-14 rounded-lg bg-gradient-to-br ${pillar.color} mb-4 group-hover:scale-110 transition-transform duration-300`}
              >
                <div className="text-jkd-black">{pillar.icon}</div>
              </div>
              <h3 className="text-lg font-bold text-white mb-3">{pillar.title}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">{pillar.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;
